import { useTranslation } from 'react-i18next';

import { TableBody, TableCell, TableRow } from '@repo/ui/components/common';

import type { MachineSidegroup } from '@/shared/types/machine.types';
import { isPrice } from '@/shared/utils/number.utils';

import { useLocaleFormatPrice, useNavigateWithLocale } from '@/client/hooks/locale.hooks';

import { LocaleLink } from '@/components/common/LocaleLink';
import { cellVariants, rowVariants, titleVariants } from '@/components/machine/MachineTable';

interface MachineTableBodyProps {
  sidegroup: MachineSidegroup;
  hasPrice: boolean;
}

export const MachineTableBody = ({ sidegroup, hasPrice }: MachineTableBodyProps) => {
  const { t } = useTranslation();
  const formatPrice = useLocaleFormatPrice();
  const navigateWithLocale = useNavigateWithLocale();

  return (
    <TableBody className="w-full flex-1">
      <TableRow className={titleVariants()}>
        <TableCell className="whitespace-normal" colSpan={6}>
          {sidegroup.title}
        </TableCell>
      </TableRow>
      {sidegroup?.machines?.map((machine, index) => (
        <TableRow
          key={machine.id}
          role="link"
          tabIndex={0}
          className={rowVariants({ isEven: index % 2 === 0 })}
          data-testid="machine-row"
          data-machine-id={machine.id}
          data-has-price={isPrice(machine.price) && machine.price > 0}
          aria-label={`${t('machine.configure')}: ${machine.title}`}
          onClick={(e) => {
            if (!(e.target instanceof HTMLAnchorElement)) {
              e.preventDefault();
              navigateWithLocale(`/configure/${machine.id}`);
            }
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              navigateWithLocale(`/configure/${machine.id}`);
            }
          }}
        >
          <TableCell className={cellVariants({ title: true })}>
            <LocaleLink
              className="focus-visible:ring-ring/50 hover:underline focus-visible:ring-[3px]"
              to={`/configure/${machine.id}`}
            >
              {machine.title}
            </LocaleLink>
          </TableCell>
          <TableCell className={cellVariants({ ident: true })}>
            {machine.ident ?? t('common.notAvailable')}
          </TableCell>
          <TableCell className={cellVariants({ width: true })}>
            {machine.details?.workingWidth ?? t('common.notAvailable')}
          </TableCell>
          <TableCell className={cellVariants({ weight: true })}>
            {machine.details?.weight ?? t('common.notAvailable')}
          </TableCell>
          <TableCell className={cellVariants({ power: true })}>
            {machine.details?.power ?? t('common.notAvailable')}
          </TableCell>
          {hasPrice ? (
            <TableCell className={cellVariants({ price: true })}>
              <LocaleLink
                to={`/configure/${machine.id}`}
                className="focus-visible:ring-ring/50 hover:underline focus-visible:ring-[3px]"
              >
                {isPrice(machine.price) && machine.price > 0
                  ? formatPrice(machine.price)
                  : t('common.notAvailable')}
              </LocaleLink>
            </TableCell>
          ) : (
            <TableCell className={cellVariants({ action: true })}>
              <LocaleLink
                to={`/configure/${machine.id}`}
                className="focus-visible:ring-ring/50 hover:underline focus-visible:ring-[3px]"
              >
                {t('machine.configure')}
              </LocaleLink>
            </TableCell>
          )}
        </TableRow>
      ))}
    </TableBody>
  );
};
